import {Badge, Button, Divider, Paper, Progress, Stack, Table, Text} from "@mantine/core";
import {ProductionPlannerRun} from "../types.ts";
import {PlannerPlanStatusSummary, RunListSummary} from "../plannerTypes.ts";
import {
    buildEmptyPlanStatusSummary,
    formatDateTime,
    formatNumber,
    formatRelativeDuration,
    getPlanProgressStatus
} from "../plannerUtils.ts";

type ProductionPlannerRunsTableProps = {
    runs: ProductionPlannerRun[];
    summaries: Map<string, RunListSummary>;
    nowSeconds: number;
    reorderPending: boolean;
    onMove: (fromIndex: number, toIndex: number) => void;
    onOpen: (runId: string) => void;
};

function getRunPlanSummary(run: ProductionPlannerRun): PlannerPlanStatusSummary {
    if (run.matchSummary) {
        return {
            total: run.matchSummary.total ?? 0,
            active: run.matchSummary.active ?? 0,
            queued: run.matchSummary.queued ?? 0,
            completed: run.matchSummary.completed ?? 0,
            pending: run.matchSummary.pending ?? run.matchSummary.total ?? 0,
        };
    }
    if (!run.planItems || run.planItems.length === 0) {
        return buildEmptyPlanStatusSummary();
    }

    return run.planItems.reduce<PlannerPlanStatusSummary>((summary, item) => {
        summary.total += 1;
        if (item.status === "active" || item.status === "queued" || item.status === "completed") {
            summary[item.status] += 1;
        } else {
            summary.pending += 1;
        }
        return summary;
    }, buildEmptyPlanStatusSummary());
}

export default function ProductionPlannerRunsTable({
    runs,
    summaries,
    nowSeconds,
    reorderPending,
    onMove,
    onOpen,
}: ProductionPlannerRunsTableProps) {
    return (
        <Paper withBorder p="md">
            <Text fw={700}>Planned Runs</Text>
            <Divider my="xs"/>
            <Table.ScrollContainer minWidth={200}>
            <Table withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>#</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Blocks</Table.Th>
                        <Table.Th>Plan</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Progress</Table.Th>
                        <Table.Th>Max ETA</Table.Th>
                        <Table.Th>Created</Table.Th>
                        <Table.Th>Updated</Table.Th>
                        <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {runs.map((run, runIndex) => {
                        const summary = summaries.get(run.id);
                        const products = summary?.products ?? [];
                        const planSummary = getRunPlanSummary(run);
                        const overallStatus = getPlanProgressStatus(planSummary);
                        const progressTotal = planSummary.total || 1;
                        const completedPercent = (planSummary.completed / progressTotal) * 100;
                        const activePercent = (planSummary.active / progressTotal) * 100;
                        const maxEtaText = summary?.etaUnknown
                            ? "Unknown"
                            : summary?.etaMaxAt
                                ? formatRelativeDuration(summary.etaMaxAt - nowSeconds)
                                : "-";

                        return (
                            <Table.Tr key={run.id}>
                                <Table.Td>{runIndex + 1}</Table.Td>
                                <Table.Td>{run.name}</Table.Td>
                                <Table.Td>{run.blocks.length}</Table.Td>
                                <Table.Td>
                                    <Stack gap={2}>
                                        {products.slice(0, 3).map((product) => (
                                            <Text key={product.key} size="xs">
                                                {product.label} x{formatNumber(product.totalUnits)}
                                            </Text>
                                        ))}
                                        {products.length > 3 ? (
                                            <Text size="xs" c="dimmed">+{products.length - 3} more</Text>
                                        ) : null}
                                        {products.length === 0 ? (
                                            <Text size="xs" c="dimmed">No entities planned</Text>
                                        ) : null}
                                    </Stack>
                                </Table.Td>
                                <Table.Td>
                                    <Badge color={overallStatus.color} variant="light">
                                        {overallStatus.label}
                                    </Badge>
                                </Table.Td>
                                <Table.Td>
                                    {planSummary.active > 0 || planSummary.completed > 0 ? (
                                        <Progress.Root size="xl" w={150}>
                                            <Progress.Section value={completedPercent} color="green">
                                                {planSummary.completed > 0 ? (
                                                    <Progress.Label>{planSummary.completed}</Progress.Label>
                                                ) : null}
                                            </Progress.Section>
                                            <Progress.Section value={activePercent} color="yellow">
                                                <Progress.Label>{planSummary.active} / {planSummary.total}</Progress.Label>
                                            </Progress.Section>
                                        </Progress.Root>
                                    ) : (
                                        <Text size="xs" c="dimmed">-</Text>
                                    )}
                                </Table.Td>
                                <Table.Td>{maxEtaText}</Table.Td>
                                <Table.Td>{formatDateTime(run.createdAt)}</Table.Td>
                                <Table.Td>{formatDateTime(run.updatedAt)}</Table.Td>
                                <Table.Td>
                                    <Button
                                        size="compact-xs"
                                        variant="light"
                                        disabled={reorderPending || runIndex === 0}
                                        onClick={() => onMove(runIndex, runIndex - 1)}
                                    >
                                        Up
                                    </Button>
                                    <Button
                                        size="compact-xs"
                                        variant="light"
                                        disabled={reorderPending || runIndex === runs.length - 1}
                                        ml={4}
                                        onClick={() => onMove(runIndex, runIndex + 1)}
                                    >
                                        Down
                                    </Button>
                                    <Button
                                        size="compact-sm"
                                        variant="light"
                                        ml={4}
                                        onClick={() => onOpen(run.id)}
                                    >
                                        Open
                                    </Button>
                                </Table.Td>
                            </Table.Tr>
                        );
                    })}
                    {runs.length === 0 ? (
                        <Table.Tr>
                            <Table.Td colSpan={10}>
                                <Text size="sm" c="dimmed">No runs yet. Create one to start planning.</Text>
                            </Table.Td>
                        </Table.Tr>
                    ) : null}
                </Table.Tbody>
            </Table>
            </Table.ScrollContainer>
        </Paper>
    );
}
